import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, Users, MessageSquare, Shield, LogOut, SquareArrowUp, User, ShieldAlert, ShieldCheck, Compass, Bookmark, HeartHandshake, Video, Radio, Menu, X, Lock, HelpCircle } from "lucide-react";
import { useOrientation } from "./OrientationTour";
import { useAuth } from "../context/AuthContext";
import { useRealtime } from "../context/RealtimeContext";
import { Crest } from "./Media";
import { NotificationBell } from "./NotificationBell";
import EncryptionInfo from "./EncryptionInfo";
import { useActiveScreenTip } from "./CoachTip";
import api from "../lib/api";

const navItems = [
  { to: "/feed", label: "The Signal", icon: Home, testid: "nav-feed" },
  { to: "/stream", label: "Conscious Stream", icon: Radio, testid: "nav-stream" },
  { to: "/directory", label: "Sovereigns", icon: Users, testid: "nav-directory" },
  { to: "/saved", label: "Saved", icon: Bookmark, testid: "nav-saved" },
  { to: "/messages", label: "Exchange", icon: MessageSquare, testid: "nav-messages" },
  { to: "/council", label: "Contact the Council", icon: ShieldAlert, testid: "nav-council" },
  { to: "/meet", label: "Encrypted Meet Up", icon: Video, testid: "nav-meet" },
];

export const AppShell = ({ children, onCompose }) => {
  const { user, logout } = useAuth();
  const { helpTour } = useOrientation();
  const { event } = useRealtime();
  const navigate = useNavigate();
  const [msgUnread, setMsgUnread] = useState(0);
  const [pending, setPending] = useState(0);
  const [meetLive, setMeetLive] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [encInfo, setEncInfo] = useState(false);
  const [watchInbox, setWatchInbox] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false);
  const transmitGlow = useActiveScreenTip("feed") === "transmit-intro";
  const compassGlow = useActiveScreenTip("feed") === "compass-intro";
  const streamGlow = useActiveScreenTip("feed") === "stream-nav";

  const loadUnread = async () => {
    try { const { data } = await api.get("/messages-unread-count"); setMsgUnread(data.count); } catch {}
    try { const { data } = await api.get("/meet/rooms"); setMeetLive((data || []).length > 0); } catch {}
    if (user?.role === "admin") {
      try { const { data } = await api.get("/admin/pending-count"); setPending(data.count); } catch {}
      const cp = user.council_perms || [];
      if (user.is_founder || cp.includes("watch") || cp.includes("disturbances")) {
        try { const { data } = await api.get("/admin/watch-inbox/unread-count"); setWatchInbox(data.total); } catch {}
      }
    }
  };

  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 30000);
    return () => clearInterval(t);
  }, []);

  // instant update when a realtime message arrives
  useEffect(() => {
    if (event && event.type === "message") loadUnread();
  }, [event]);

  const moreDot = msgUnread > 0 || (user?.role === "admin" && (pending + watchInbox) > 0);
  const primaryMobile = [
    { to: "/feed", short: "Signal", icon: Home, testid: "nav-feed" },
    { to: "/stream", short: "Stream", icon: Radio, testid: "nav-stream" },
    { to: `/profile/${user?.id}`, short: "My HQ", icon: User, testid: "nav-hq" },
    { to: "/directory", short: "Sovereigns", icon: Users, testid: "nav-directory" },
    { to: "/meet", short: "Meet Up", icon: Video, testid: "nav-meet" },
  ];
  const moreItems = [
    { to: "/messages", label: "Exchange", icon: MessageSquare, testid: "nav-messages-more", badge: msgUnread, color: "#A855F7" },
    { to: "/hearths", label: "Communities", icon: Lock, testid: "nav-hearths-more", color: "#14A89A" },
    { to: "/saved", label: "Saved", icon: Bookmark, testid: "nav-saved-more", color: "#14A89A" },
    { to: "/council", label: "Contact the Council", icon: ShieldAlert, testid: "nav-council-more", color: "#A855F7" },
    { to: "/compass", label: "Compass", icon: Compass, testid: "nav-compass-more", color: "#A855F7" },
    { to: "/donate", label: "Donate", icon: HeartHandshake, testid: "nav-donate-more", color: "#14A89A" },
    ...(user?.role === "admin" ? [{ to: "/admin", label: "The Council", icon: Shield, testid: "nav-admin-more", badge: pending + watchInbox, color: "#A855F7" }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r border-white/10 fixed h-screen p-6 justify-between">
        <div>
          <div className="flex items-center justify-between mb-12">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/feed")} data-testid="sidebar-logo">
              <Crest className="h-10 w-10" />
              <div className="leading-tight">
                <div className="font-heading text-sm tracking-tight text-white">SOVEREIGN</div>
                <div className="font-body text-[10px] tracking-[0.35em] text-[#14A89A]">SIGNAL</div>
              </div>
            </div>
            <NotificationBell />
          </div>
          <nav className="flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-3 transition-colors font-body text-sm ${
                    isActive ? "text-white bg-white/5 border-l-2 border-[#14A89A]" : "text-zinc-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
                  } ${item.testid === "nav-stream" && streamGlow ? "stream-pulse" : ""}`
                }
              >
                <item.icon strokeWidth={1.5} size={18} />
                {item.label}
                {item.to === "/messages" && msgUnread > 0 && (
                  <span data-testid="messages-unread-badge" className="ml-auto bg-[#14A89A] text-[#0A0A0A] text-[10px] font-bold min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full">{msgUnread > 9 ? "9+" : msgUnread}</span>
                )}
                {item.to === "/meet" && meetLive && (
                  <span data-testid="meet-live-badge" className="ml-auto flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#14A89A] animate-pulse" />
                    <span className="font-body text-[9px] uppercase tracking-widest text-[#14A89A]">Live</span>
                  </span>
                )}
              </NavLink>
            ))}
            {user?.role === "admin" && (
              <NavLink
                to="/admin"
                data-testid="nav-admin"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-3 transition-colors font-body text-sm ${
                    isActive ? "text-white bg-white/5 border-l-2 border-[#14A89A]" : "text-zinc-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
                  }`
                }
              >
                <Shield strokeWidth={1.5} size={18} />
                Council
                {(pending + watchInbox) > 0 && (
                  <span data-testid="pending-badge" className="ml-auto bg-[#A855F7] text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full">{(pending + watchInbox) > 9 ? "9+" : (pending + watchInbox)}</span>
                )}
              </NavLink>
            )}
          </nav>

          <button
            onClick={onCompose}
            data-testid="compose-button"
            className={`mt-8 w-full bg-[#14A89A] text-[#0A0A0A] font-medium px-4 py-3 hover:bg-[#2BC4B5] transition-colors flex items-center justify-center gap-2 font-body text-sm ${transmitGlow ? "transmit-pulse" : ""}`}
          >
            <SquareArrowUp strokeWidth={2} size={16} /> Transmit
          </button>
        </div>

        <div className="border-t border-white/10 pt-4">
          <NavLink to="/hearths" data-testid="nav-hearths" className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 font-body text-sm">
            <Lock strokeWidth={1.5} size={16} className="text-[#14A89A]" /> Communities
          </NavLink>
          <button onClick={helpTour} data-testid="nav-help" className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 font-body text-sm">
            <HelpCircle strokeWidth={1.5} size={16} className="text-zinc-300" /> Signal tour
          </button>
          <NavLink to="/compass" data-testid="nav-compass" className={`flex items-center gap-2 text-zinc-400 hover:text-white mb-4 font-body text-sm ${compassGlow ? "compass-pulse" : ""}`}>
            <Compass strokeWidth={1.5} size={16} className="text-[#A855F7]" /> Compass
          </NavLink>
          <NavLink to="/donate" data-testid="nav-donate" className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 font-body text-sm">
            <HeartHandshake strokeWidth={1.5} size={16} className="text-[#14A89A]" /> Donate
          </NavLink>
          <button onClick={() => setEncInfo(true)} data-testid="encryption-badge" className="flex items-center gap-2 text-[#14A89A]/80 hover:text-[#14A89A] mb-4 font-body text-[10px] tracking-[0.15em] uppercase transition-colors">
            <ShieldCheck strokeWidth={1.5} size={14} /> End-to-end encrypted
          </button>
          <NavLink to={`/profile/${user?.id}`} data-testid="nav-profile" className="flex items-center gap-3 text-zinc-300 hover:text-white mb-3 font-body text-sm">
            <User strokeWidth={1.5} size={18} />
            <span className="truncate">{user?.name}</span>
          </NavLink>
          <button onClick={() => setConfirmLogout(true)} data-testid="logout-button" className="flex items-center gap-3 text-zinc-500 hover:text-white font-body text-xs tracking-wider uppercase">
            <LogOut strokeWidth={1.5} size={16} /> Disconnect
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/10 flex items-center justify-between px-4 h-14">
        <div className="flex items-center gap-2" onClick={() => navigate("/feed")}>
          <Crest className="h-7 w-7" />
          <span className="font-heading text-sm text-white">SOVEREIGN SIGNAL</span>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={helpTour} data-testid="nav-help-mobile" aria-label="Signal tour" title="Signal tour" className="text-zinc-300 hover:text-white"><HelpCircle size={20} /></button>
          <NavLink to="/compass" data-testid="nav-compass-mobile" className={`text-[#A855F7] rounded-full ${compassGlow ? "compass-pulse" : ""}`}><Compass size={20} /></NavLink>
          {user?.role === "admin" && (
            <button onClick={() => navigate("/admin")} data-testid="nav-admin-mobile" title="The Council" className="relative text-[#14A89A]">
              <Shield size={20} />
              {(pending + watchInbox) > 0 && <span data-testid="pending-badge-mobile" className="absolute -top-1.5 -right-1.5 bg-[#A855F7] text-white text-[9px] font-bold min-w-[15px] h-[15px] px-0.5 flex items-center justify-center rounded-full">{(pending + watchInbox) > 9 ? "9+" : (pending + watchInbox)}</span>}
            </button>
          )}
          <NotificationBell mobile />
          <button onClick={onCompose} data-testid="compose-button-mobile" className={`text-[#14A89A] rounded-full ${transmitGlow ? "transmit-pulse" : ""}`}><SquareArrowUp size={22} /></button>
        </div>
      </header>

      {/* Mobile bottom nav, 4 primary tabs + More */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 backdrop-blur-xl bg-[#0A0A0A]/90 border-t border-white/10 flex justify-around items-center h-16">
        {primaryMobile.map((item) => (
          <NavLink key={item.to} to={item.to} data-testid={`${item.testid}-bottom`} className={({ isActive }) => `flex flex-col items-center gap-0.5 ${isActive ? "text-[#A855F7]" : "text-zinc-500"} ${item.testid === "nav-stream" && streamGlow ? "stream-pulse" : ""}`}>
            <span className="relative">
              <item.icon strokeWidth={1.5} size={20} />
              {item.to === "/meet" && meetLive && (
                <span data-testid="meet-live-badge-bottom" className="absolute -top-1 -right-1.5 w-2.5 h-2.5 rounded-full bg-[#14A89A] animate-pulse ring-2 ring-[#0A0A0A]" />
              )}
            </span>
            <span className="font-body text-[9px] leading-none tracking-wide">{item.short}</span>
          </NavLink>
        ))}
        <button onClick={() => setMoreOpen(true)} data-testid="nav-more-bottom" aria-label="More" className="relative flex flex-col items-center gap-0.5 text-zinc-500">
          <span className="relative">
            <Menu strokeWidth={1.5} size={20} />
            {moreDot && <span data-testid="more-dot" className="absolute -top-1 -right-1.5 w-2.5 h-2.5 rounded-full bg-[#A855F7] ring-2 ring-[#0A0A0A]" />}
          </span>
          <span className="font-body text-[9px] leading-none tracking-wide">More</span>
        </button>
      </nav>

      {/* Mobile "More" bottom sheet */}
      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-[80]" data-testid="more-sheet">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMoreOpen(false)} />
          <div className="absolute bottom-0 inset-x-0 bg-[#111111] border-t border-white/10 rounded-t-2xl p-4 pb-8 fade-up">
            <div className="flex items-center justify-between mb-2">
              <span className="font-body text-[11px] tracking-[0.2em] uppercase text-zinc-500">More</span>
              <button onClick={() => setMoreOpen(false)} data-testid="more-sheet-close" className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white"><X size={16} /></button>
            </div>
            <div className="divide-y divide-white/5">
              {moreItems.map((it) => (
                <button key={it.to} onClick={() => { setMoreOpen(false); navigate(it.to); }} data-testid={it.testid} className="w-full flex items-center gap-3 py-3 text-left text-zinc-200 hover:text-white transition-colors">
                  <it.icon strokeWidth={1.5} size={19} style={it.color ? { color: it.color } : {}} />
                  <span className="font-body text-sm flex-1">{it.label}</span>
                  {it.badge > 0 && <span className="bg-[#A855F7] text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full">{it.badge > 9 ? "9+" : it.badge}</span>}
                </button>
              ))}
              <button onClick={() => { setMoreOpen(false); setEncInfo(true); }} data-testid="more-encryption" className="w-full flex items-center gap-3 py-3 text-left text-[#14A89A]/90 hover:text-[#14A89A] transition-colors">
                <ShieldCheck strokeWidth={1.5} size={19} />
                <span className="font-body text-sm flex-1">End-to-end encrypted</span>
              </button>
              <button onClick={() => { setMoreOpen(false); setConfirmLogout(true); }} data-testid="more-logout" className="w-full flex items-center gap-3 py-3 text-left text-zinc-400 hover:text-white transition-colors">
                <LogOut strokeWidth={1.5} size={19} />
                <span className="font-body text-sm flex-1">Disconnect</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 overflow-x-hidden md:ml-64 pt-14 md:pt-0 pb-20 md:pb-0 min-h-screen">{children}</main>

      {confirmLogout && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="logout-confirm-modal">
          <div className="bg-[#121212] border border-[#14A89A]/40 w-full max-w-md p-8">
            <ShieldAlert className="text-[#14A89A] mb-4" strokeWidth={1.5} />
            <h3 className="font-heading text-2xl text-white mb-3">Before you disconnect</h3>
            <p className="font-body text-sm text-zinc-400 leading-relaxed mb-2">
              Disconnecting wipes your encryption keys from this browser. To return on this or any other device, you'll need your <span className="text-white">password</span> or your <span className="text-white">12-word recovery phrase</span>.
            </p>
            <p className="font-body text-sm text-zinc-400 leading-relaxed mb-8">
              If you haven't saved your recovery phrase, your encrypted vault <span className="text-[#14A89A]">cannot be recovered</span>. Are you sure?
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmLogout(false)} data-testid="logout-cancel-button" className="flex-1 border border-white/15 text-zinc-300 px-5 py-3 font-body text-sm hover:bg-white/5 transition-colors">
                Stay
              </button>
              <button onClick={() => { setConfirmLogout(false); logout(); }} data-testid="logout-confirm-button" className="flex-1 bg-[#14A89A] text-[#0A0A0A] px-5 py-3 font-body text-sm font-medium hover:bg-[#2BC4B5] transition-colors">
                I've saved it - disconnect
              </button>
            </div>
          </div>
        </div>
      )}
      <EncryptionInfo open={encInfo} onClose={() => setEncInfo(false)} />
    </div>
  );
};
